import type { SearchFilters, SearchResult } from './types';
import type { OffMarketWillingnessEvent } from '../utils/types';
import type { AreaDemandCard } from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';

export type DetailField = 'size' | 'rooms' | 'floor' | 'renovationStatus' | 'hasElevator' | 'hasBalcony';
export type MatchStatus = 'confirmed' | 'unconfirmed' | 'excluded';
export type MatchAssessment = {
  status: MatchStatus;
  unconfirmedFields: DetailField[];
  kind?: 'seller' | 'buyer';
};

type Listing = SearchResult | OffMarketWillingnessEvent;
type BuyerCriteria = NonNullable<OffMarketWillingnessEvent['criteria']>;

const fields: DetailField[] = ['size', 'rooms', 'floor', 'renovationStatus', 'hasElevator', 'hasBalcony'];

export const activeDetailFields = (filters: SearchFilters): DetailField[] => fields.filter((field) => {
  if (field === 'size' || field === 'floor') {
    return number(filters[field].min) !== null || number(filters[field].max) !== null;
  }
  if (field === 'rooms') return rooms(filters.rooms) !== null;
  if (field === 'renovationStatus') return condition(filters.renovationStatus) !== null;
  return filters[field] === true;
});

const number = (value: unknown, integer = false): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && (!integer || (parsed >= 1 && Number.isInteger(parsed))) ? parsed : null;
};

const rooms = (value: unknown): number | null => typeof value === 'string' && value.trim() === '5+' ? 5 : number(value, true);
/** Shared with buyer-card labels so malformed minima cannot disagree with filtering. */
export const parseRoomCount = rooms;
const condition = (value: unknown): string | null => {
  const normalized = value === 'good' ? 'good_condition' : value;
  return typeof normalized === 'string' &&
    ['recently_renovated', 'good_condition', 'needs_renovation'].includes(normalized)
    ? normalized : null;
};

const buyerValue = (listing: Listing, key: keyof BuyerCriteria, legacy: keyof Listing): unknown => {
  const criteria = listing.criteria;
  return criteria && typeof criteria === 'object' && !Array.isArray(criteria)
    ? criteria[key] : listing[legacy];
};

export const classifyDetails = (
  listing: Listing | AreaDemandCard,
  filters: SearchFilters,
  kind: 'seller' | 'buyer',
): MatchAssessment => {
  const isArea = 'area_interest_id' in listing;
  const source = listing as Listing;
  const get = (key: keyof BuyerCriteria, legacy: keyof Listing): unknown =>
    isArea ? (listing as AreaDemandCard).criteria?.[key] : buyerValue(source, key, legacy);
  const unknown: DetailField[] = [];
  for (const field of activeDetailFields(filters)) {
    let status: MatchStatus = 'confirmed';
    if (field === 'size') {
      const low = number(filters.size.min);
      const high = number(filters.size.max);
      if (kind === 'seller') {
        const value = number(source.property_size);
        status = value === null ? 'unconfirmed'
          : (low !== null && value < low) || (high !== null && value > high) ? 'excluded' : 'confirmed';
      } else {
        const min = number(get('size_min', 'property_size'));
        const max = number(get('size_max', 'property_size_max'));
        status = (min === null && max === null) || (min !== null && max !== null && min > max)
          ? 'unconfirmed'
          : (high !== null && min !== null && min > high) || (low !== null && max !== null && max < low)
            ? 'excluded' : 'confirmed';
      }
    } else if (field === 'rooms') {
      const wanted = rooms(filters.rooms);
      if (kind === 'seller') {
        const value = rooms(source.property_rooms);
        status = value === null ? 'unconfirmed'
          : (filters.rooms === '5+' ? value < 5 : value !== wanted) ? 'excluded' : 'confirmed';
      } else {
        const min = rooms(get('rooms_min', 'property_rooms'));
        const flexible = get('rooms_flexible', 'property_rooms_flexible') === true;
        status = min === null ? 'unconfirmed'
          : filters.rooms !== '5+' && Math.max(1, min - Number(flexible)) > (wanted ?? 0)
            ? 'excluded' : 'confirmed';
      }
    } else if (field === 'floor') {
      const value = number(kind === 'seller' ? source.property_floor : get('floor', 'property_floor'));
      status = value === null ? 'unconfirmed'
        : (filters.floor.min !== null && value < filters.floor.min) ||
          (filters.floor.max !== null && value > filters.floor.max) ? 'excluded' : 'confirmed';
    } else if (field === 'renovationStatus') {
      const value = condition(kind === 'seller' ? source.property_renovation_status
        : get('renovation_status', 'property_renovation_status'));
      status = value === null ? 'unconfirmed'
        : value !== condition(filters.renovationStatus) ? 'excluded' : 'confirmed';
    } else {
      const key = field === 'hasElevator' ? 'property_has_elevator' : 'property_has_balcony';
      const amenity = field === 'hasElevator' ? 'elevator' : 'balcony';
      if (kind === 'seller') {
        status = source[key] === true ? 'confirmed' : 'unconfirmed';
      } else if (isArea || (source.criteria && typeof source.criteria === 'object' && !Array.isArray(source.criteria))) {
        const amenities = get('amenities', key);
        status = Array.isArray(amenities) && amenities.includes(amenity) ? 'confirmed' : 'unconfirmed';
      } else {
        status = source[key] === true ? 'confirmed' : 'unconfirmed';
      }
    }
    if (status === 'excluded') return { status, unconfirmedFields: [] };
    if (status === 'unconfirmed') unknown.push(field);
  }
  return { status: unknown.length ? 'unconfirmed' : 'confirmed', unconfirmedFields: unknown };
};

export const classifyWillingness = (listing: Listing, filters: SearchFilters): MatchAssessment => {
  const sides: MatchAssessment[] = [];
  if (filters.listingType !== 'buy' && listing.could_sell === true) {
    sides.push({ ...classifyDetails(listing, filters, 'seller'), kind: 'seller' });
  }
  if (filters.listingType !== 'sell' && listing.could_buy === true) {
    sides.push({ ...classifyDetails(listing, filters, 'buyer'), kind: 'buyer' });
  }
  return sides.find((side) => side.status === 'confirmed') ??
    sides.find((side) => side.status === 'unconfirmed') ??
    { status: 'excluded', unconfirmedFields: [] };
};

export const unconfirmedDetailsLabel = (
  fields: readonly string[] | undefined,
  t: (key: string) => string,
  kind: 'seller' | 'buyer',
): string => (fields ?? []).map((field) => t(`exploreListings.unconfirmedField.${kind}.${field}`)).join(' · ');
